import React from 'react';
import FileUploadStep from '../components/FileUploadStep';
import SheetMappingStep from '../components/SheetMappingStep';
import SummaryStep from '../components/SummaryStep';
import MappingsSelectionStep from '../components/MappingsSelectionStep';
import ImportConfirmation from '../components/ImportConfirmation';
import AccordionSection from '../components/AccordionSection';
import { useProcessorState } from '../hooks/useProcessorState';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CalendarRange, Calendar, Store, User, Package, File } from 'lucide-react';

function Processor() {
  const { currentUser } = useAuth();
  const { state, actions } = useProcessorState();

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-8xl mx-auto">
        {/* History Button */}
        <div className="mb-6 flex justify-end gap-3">
          <a
            href="/processor/analysis"
            className="px-4 py-2 bg-white text-blue-600 border border-blue-500 rounded hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            target="_blank"
            rel="noopener noreferrer"
          >
            Analyse
          </a>
          <a
            href="/processor/history"
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
            target="_blank"
            rel="noopener noreferrer"
          >
            History
          </a>
        </div>

        <AccordionSection
          title="1. Sélection du fichier"
          isOpen={state.currentStep === 1}
          isCompleted={state.completedSteps.has(1)}
          canOpen={true}
          showBackButton={false}
          InfoChildren={
            <div className="ml-10 flex flex-wrap items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
                <span className="flex items-center gap-1 bg-gray-50 dark:bg-yellow-900/30 px-2 py-1 rounded-md border border-yellow-100 dark:border-yellow-800/50">
                  <CalendarRange className="w-4 h-4 text-yellow-500" />
                  <span className="font-medium">Année</span> {state.year}
                </span>
                <span className="flex items-center gap-1 bg-blue-50 dark:bg-blue-900/30 px-2 py-1 rounded-md border border-blue-100 dark:border-blue-800/50">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <span className="font-medium">Semaine</span> {state.week}
                </span>
                <span className="flex items-center gap-1 bg-purple-50 dark:bg-purple-900/30 px-2 py-1 rounded-md border border-purple-100 dark:border-purple-800/50">
                  <Store className="w-4 h-4 text-purple-500" />
                  <span className="font-medium">Enseigne</span> {state.shopSign}
                </span>
                <span className="flex items-center gap-1 bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded-md border border-green-100 dark:border-green-800/50">
                  <User className="w-4 h-4 text-green-500" />
                  <span className="font-medium">Client</span> {state.client}
                </span>
              {state.category && (
                <span className="flex items-center gap-1 bg-orange-50 dark:bg-orange-900/30 px-2 py-1 rounded-md border border-orange-100 dark:border-orange-800/50">
                  <Package className="w-4 h-4 text-orange-500" />
                  <span className="font-medium">Univers</span> {state.category}
                </span>
              )}
              <span className="flex items-center gap-1 bg-cyan-50 dark:bg-cyan-900/30 px-2 py-1 rounded-md border border-cyan-100 dark:border-cyan-800/50">
                <File className="w-4 h-4 text-cyan-500" />
                <span className="font-medium">Fichier</span> {state.excelName || state.fileName}
              </span>
            </div>
          }
        >
          <FileUploadStep
            onUploadSuccess={actions.handleUploadSuccess}
            initialData={{
              shopSign: state.shopSign,
              client: state.client,
              year: state.year,
              week: state.week,
              category: state.category,
              uploadedFile: state.uploadedFile
            }}
            currentUser={currentUser}
          />
        </AccordionSection>

        <AccordionSection
          title="2. Configurations existantes"
          isOpen={state.currentStep === 2}
          isCompleted={state.completedSteps.has(2)}
          canOpen={state.completedSteps.has(1)}
          onBack={actions.handleBack}
          showBackButton={true}
        >
          <MappingsSelectionStep
            mappings={state.mappings}
            shopSign={state.shopSign}
            client={state.client}
            onMappingSelect={actions.handleMappingSelect}
            onDeleteMapping={actions.handleDeleteMapping}
            onContinueManual={actions.handleContinueManual}
            onBack={actions.handleBack}
            currentUser={currentUser}
            hasMappings={state.mappings?.length > 0}
          />
        </AccordionSection>

        <AccordionSection
          title="3. Configuration des colonnes"
          isOpen={state.currentStep === 3}
          isCompleted={state.completedSteps.has(3)}
          canOpen={state.completedSteps.has(1) && (state.completedSteps.has(2) || !state.mappings?.length)}
          onBack={actions.handleBack}
          showBackButton={true}
        >
          <SheetMappingStep
            fileName={state.fileName}
            sheets={state.sheets}
            shopSign={state.shopSign}
            client={state.client}
            year={state.year}
            week={state.week}
            category={state.category}
            onBack={actions.handleBack}
            onNext={actions.handleNext}
            initialData={{
              sheetConfigs: state.sheetConfigs,
              mappingChoices: state.mappingChoices
            }}
            currentUser={currentUser}
          />
        </AccordionSection>

        <AccordionSection
          title="4. Récapitulatif"
          isOpen={state.currentStep === 4}
          isCompleted={state.completedSteps.has(4)}
          canOpen={state.completedSteps.has(3)}
          onBack={actions.handleBack}
          showBackButton={true}
        >
          <SummaryStep
            sheetConfigs={state.sheetConfigs}
            mappingChoices={state.mappingChoices}
            onBack={actions.handleBack}
            onSubmit={(confirmedAnomalies) => {
              console.log('onSubmit', state.sheetConfigs, state.selectedMapping, state);
              return actions.handleSubmit(state.sheetConfigs, state.selectedMapping, confirmedAnomalies);
            }}
            fileName={state.fileName}
            shopSign={state.shopSign}
            client={state.client}
            year={state.year}
            week={state.week}
            currentUser={currentUser}
            category={state.category}
            error={state.error}
          />
        </AccordionSection>

        <AccordionSection
          title="5. Confirmation"
          isOpen={state.currentStep === 5}
          isCompleted={state.completedSteps.has(5)}
          canOpen={state.completedSteps.has(4)}
          showBackButton={false}
        >
          <ImportConfirmation
            onRestart={actions.handleRestart}
            jobInfo={state.latestJob}
          />
        </AccordionSection>
      </div>

      
    </div>
  );
}

export default Processor;
